import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyword } from '@connect-x-app/shared-domain-models';
import { FormValidator } from '@connect-x-app/shared-utils';
import { FormValidationErrors, useForm } from '@connect-x-app/web-state';
import { InputField, Modal } from '@connect-x-app/web-ui';

import { Button, FlexComponent } from '../../../../../shared';

type Props = {
  closeModal: () => void;
  onsubmit: (keyword: Keyword) => void;
};

const validateDescription = (validator: FormValidator, description = '') => {
  const requiredValidation = validator.required(description);
  if (!requiredValidation.isValid) {
    return requiredValidation;
  }

  const maxLengthValidation = validator.maxLength(description, 100);

  if (!maxLengthValidation.isValid) {
    return maxLengthValidation;
  }

  return undefined;
};

const validateKeyword = (validator: FormValidator, keyword: string) => {
  const requiredValidation = validator.required(keyword);
  if (!requiredValidation.isValid) {
    return requiredValidation;
  }

  const maxLengthValidation = validator.maxLength(keyword, 100);

  if (!maxLengthValidation.isValid) {
    return maxLengthValidation;
  }

  return undefined;
};

const validateForm = (values: Keyword): FormValidationErrors<Keyword> => {
  const validator = new FormValidator();

  return {
    description: validateDescription(validator, values?.description),
    keyword: validateKeyword(validator, values?.keyword),
  };
};

const initialForm: Keyword = {
  description: '',
  keyword: '',
};

export const AddKeywordModal: FC<Props> = ({ closeModal, onsubmit: submitForm }) => {
  const { t } = useTranslation();

  const form = useForm<Keyword>({
    initialForm,
    submitForm: values => submitForm(values),
    validateForm: values => validateForm(values),
  });

  return (
    <Modal>
      <Modal.Header onCancel={closeModal}>{t('SMART_ACTION_FORM_KEYWORDS_ADD_BUTTON')}</Modal.Header>
      <form onSubmit={form.submit}>
        <FlexComponent flexDirection="column" gap={1} padding={2} width="full">
          <InputField
            label="text"
            name="keyword"
            onChange={form.setAttribute}
            required
            showInlineBottomError
            validation={form.validationErrors.keyword}
            value={form.values?.keyword}
          />
          <InputField
            label="description"
            name="description"
            onChange={form.setAttribute}
            required
            showInlineBottomError
            validation={form.validationErrors.description}
            value={form.values?.description}
          />
        </FlexComponent>
        <Modal.Buttons>
          <div className="buttons-left-container"></div>
          <Button isOutline onClick={closeModal} theme="transparent">
            {t('SHARED.BUTTONS.CANCEL')}
          </Button>
          <Button disabled={!form.isDirty || !form.isValid} theme="primary" type="submit">
            {t('SHARED.BUTTONS.ADD')}
          </Button>
        </Modal.Buttons>
      </form>
    </Modal>
  );
};
